// Autocomplete. What it offers depends on where the caret is:
//
//   =GI.SUM(          -> the PROJECT'S FIELDS, not ABS/AND/AVERAGE
//   =GI.SUM("gross    -> fields matching "gross"
//   =GI.SUM("x","us   -> filter fields
//   =GI.SUM("x","usage=Res  -> the distinct values that field actually holds
//   =GI.SUM("x","","  -> the three sources
//   =SU               -> functions
//
// Offering function names inside a GI call is the single most useless thing
// autocomplete can do there, and it was what the first version did.

import type { GiraffeData, MeasureClass } from '@/data/types';
import { naturalCompare } from '@/model/collate';
import { distinct } from '@/data/aggregate';
import { prettyField } from '@/data/classify';
import {
  SOURCE_NAMES,
  namedSource,
  sourceExample,
  argRole,
  callContextAt,
  fieldForValueArg,
  filterClauseAt,
  sourceOf,
  type SourceName,
} from './context';
import { CATALOG, type FunctionDoc } from './catalog';

export type SuggestionKind = 'function' | 'field' | 'value' | 'source';

export interface Suggestion {
  kind: SuggestionKind;
  /** Replacement text for [start, caret). */
  insert: string;
  /** Where the caret lands, as an offset into `insert`. */
  caret: number;
  /** Monospace headline in the list. */
  label: string;
  /** Grey second line. */
  detail: string;
  /** Worked calls, shown under the detail. Functions only. */
  examples?: string[];
}

export interface SuggestResult {
  items: Suggestion[];
  /** Offset the replacement starts at. */
  start: number;
  /** What the user has typed so far, for the "already complete" check. */
  typed: string;
  heading: string | null;
  /** A line under the heading: which source the suggestions come from, and how to change it. */
  note?: string;
}

const EMPTY: SuggestResult = { items: [], start: 0, typed: '', heading: null };

const CLASS_NOTE: Record<MeasureClass, string> = {
  plan: 'plan — one level per section, sums',
  total: 'total — levels already applied, sums',
  rate: 'rate — per unit, use GI.WEIGHTEDAVG for the mean',
  text: 'text',
  nested: 'nested — shown as a summary, cannot be totalled or written back',
};

export function suggestAt(text: string, caret: number, data: GiraffeData): SuggestResult {
  if (!text.startsWith('=')) return EMPTY;

  const ctx = callContextAt(text, caret);
  const role = argRole(ctx);
  const source = sourceOf(ctx);
  const note = sourceNote(ctx.fn, namedSource(ctx));
  const withNote = (r: SuggestResult): SuggestResult => (r.items.length > 0 && note ? { ...r, note } : r);

  if (role === 'field') return withNote(fieldSuggestions(text, caret, ctx.quoted, ctx.quoteStart, ctx.argStart, data, source));
  if (role === 'source') {
    const r = sourceSuggestions(text, caret, ctx.quoted, ctx.quoteStart, ctx.argStart);
    return r.items.length > 0 ? { ...r, note: 'Leave this out to read the Areas tab, the default.' } : r;
  }
  if (role === 'filter') return withNote(filterSuggestions(text, caret, ctx.quoted, ctx.quoteStart, ctx.argStart, data, source));
  if (role === 'value') {
    return withNote(valueSuggestions(text, caret, ctx.quoted, ctx.quoteStart, ctx.argStart, fieldForValueArg(ctx), data, source));
  }

  // Not inside a GI argument — complete function names.
  return functionSuggestions(text, caret);
}

/**
 * Say where the suggestions come from. The source is the LAST argument, so
 * while the field is being typed nothing on screen says Areas is the default
 * or that baked sections are one argument away.
 */
function sourceNote(fn: string, named: SourceName | null): string | undefined {
  if (named) return `From ${prettyLabel(named)}, named in this formula. Remove "${named}" to read the Areas tab.`;
  const example = sourceExample(fn, 'bakedSections');
  if (!example) return undefined;
  return `From the Areas tab (default). For baked sections: ${example}`;
}

// ---------------------------------------------------------------------------

function functionSuggestions(text: string, caret: number): SuggestResult {
  let i = caret;
  while (i > 0 && /[A-Za-z0-9_.]/.test(text[i - 1]!)) i -= 1;
  const typed = text.slice(i, caret);
  if (typed === '' || /^[0-9]/.test(typed)) return EMPTY;
  const p = typed.toUpperCase();

  const hits = CATALOG.filter((f) => f.name.startsWith(p));
  hits.sort(byGiraffeFirst);
  return {
    items: hits.slice(0, 8).map((f) => ({
      kind: 'function' as const,
      insert: `${f.name}(`,
      caret: f.name.length + 1,
      label: f.signature,
      detail: f.summary,
      examples: f.examples,
    })),
    start: i,
    typed,
    heading: null,
  };
}

function byGiraffeFirst(a: FunctionDoc, b: FunctionDoc): number {
  if (a.group === 'Giraffe' && b.group !== 'Giraffe') return -1;
  if (b.group === 'Giraffe' && a.group !== 'Giraffe') return 1;
  return a.name.length - b.name.length || a.name.localeCompare(b.name);
}

/**
 * A string argument's replacement span. Inside quotes it starts after the
 * opening quote; outside them it starts at the argument, and the inserted text
 * brings its own quotes.
 */
function stringSpan(
  text: string,
  caret: number,
  quoted: boolean,
  quoteStart: number,
  argStart: number
): { start: number; typed: string; needsOpen: boolean; needsClose: boolean } {
  if (quoted) {
    return {
      start: quoteStart,
      typed: text.slice(quoteStart, caret),
      needsOpen: false,
      needsClose: text[caret] !== '"',
    };
  }
  const start = argStart;
  const typed = text.slice(start, caret);
  return { start, typed: typed.trimStart(), needsOpen: true, needsClose: true };
}

function wrap(value: string, span: { needsOpen: boolean; needsClose: boolean }): { insert: string; caret: number } {
  const insert = `${span.needsOpen ? '"' : ''}${value}${span.needsClose ? '"' : ''}`;
  return { insert, caret: insert.length };
}

function fieldSuggestions(
  text: string,
  caret: number,
  quoted: boolean,
  quoteStart: number,
  argStart: number,
  data: GiraffeData,
  source: SourceName
): SuggestResult {
  const span = stringSpan(text, caret, quoted, quoteStart, argStart);
  const fields = rankFields(data.fields(source), span.typed);
  if (fields.length === 0) return EMPTY;

  return {
    items: fields.slice(0, 40).map((field) => {
      const cls = data.classOf(source, field);
      const { insert, caret: c } = wrap(field, span);
      return {
        kind: 'field' as const,
        insert,
        caret: c,
        label: field,
        detail: `${prettyField(field)} · ${CLASS_NOTE[cls]}${sampleNote(data, source, field)}`,
      };
    }),
    start: span.start,
    typed: span.typed,
    heading: `${data.fields(source).length} fields in ${prettyLabel(source)}`,
  };
}

function sourceSuggestions(
  text: string,
  caret: number,
  quoted: boolean,
  quoteStart: number,
  argStart: number
): SuggestResult {
  const span = stringSpan(text, caret, quoted, quoteStart, argStart);
  const hits = SOURCE_NAMES.filter((n) => n.toLowerCase().startsWith(span.typed.toLowerCase()));
  if (hits.length === 0) return EMPTY;
  const detail: Record<SourceName, string> = {
    areas: 'The Areas tab — one row per level of each baked stack. The default.',
    bakedSections: 'The engine output — areas, unit counts, usage rates.',
    rawSections: 'What was drawn, before the engine stacked it.',
    usages: 'The project usage table — rents, costs, efficiency.',
  };
  return {
    items: hits.map((n) => {
      const { insert, caret: c } = wrap(n, span);
      return { kind: 'source' as const, insert, caret: c, label: n, detail: detail[n] };
    }),
    start: span.start,
    typed: span.typed,
    heading: 'Source',
  };
}

/**
 * The values a `GI.WHERE` pair can take — the distinct values of the field
 * named just before it.
 *
 * A cell reference is the commoner answer here (`GI.WHERE("usage", $A20)` is
 * what a schedule row wants), so this is deliberately quiet: it offers what
 * the field holds, and typing `A2` matches none of it and the list goes away.
 */
function valueSuggestions(
  text: string,
  caret: number,
  quoted: boolean,
  quoteStart: number,
  argStart: number,
  field: string,
  data: GiraffeData,
  source: SourceName
): SuggestResult {
  if (field === '') return EMPTY;
  const span = stringSpan(text, caret, quoted, quoteStart, argStart);
  const hits = rank(distinct(data.rows(source), field).map(String), span.typed);
  if (hits.length === 0) return EMPTY;

  return {
    items: hits.slice(0, 40).map((value) => {
      const { insert, caret: c } = wrap(value, span);
      return {
        kind: 'value' as const,
        insert,
        caret: c,
        label: value,
        detail: `${prettyField(field)} = ${value}`,
      };
    }),
    start: span.start,
    typed: span.typed,
    heading: `Values of ${prettyField(field)}`,
  };
}

function filterSuggestions(
  text: string,
  caret: number,
  quoted: boolean,
  quoteStart: number,
  argStart: number,
  data: GiraffeData,
  source: SourceName
): SuggestResult {
  const span = stringSpan(text, caret, quoted, quoteStart, argStart);
  const clause = filterClauseAt(span.typed);

  // Before an operator, the user is naming a field; after it, a value.
  if (clause.op === '') {
    const fields = rankFields(data.fields(source), clause.field);
    if (fields.length === 0) return EMPTY;
    return {
      items: fields.slice(0, 40).map((field) => {
        const body = `${clause.prefix}${field}=`;
        const insert = `${span.needsOpen ? '"' : ''}${body}`;
        return {
          kind: 'field' as const,
          insert,
          // Leave the caret right after `=`, ready for the value — and do NOT
          // close the quote yet, or the next keystroke lands outside the string.
          caret: insert.length,
          label: `${field}=`,
          detail: `Filter on ${prettyField(field)}${sampleNote(data, source, field)}`,
        };
      }),
      start: span.start,
      typed: clause.field,
      heading: 'Filter by',
    };
  }

  const values = distinct(data.rows(source), clause.field).map(String);
  const hits = rank(values, clause.value);
  if (hits.length === 0) return EMPTY;
  return {
    items: hits.slice(0, 40).map((value) => {
      const body = `${clause.prefix}${clause.field}${clause.op}${value}`;
      const insert = `${span.needsOpen ? '"' : ''}${body}${span.needsClose ? '"' : ''}`;
      return {
        kind: 'value' as const,
        insert,
        caret: insert.length,
        label: value,
        detail: `${prettyField(clause.field)} ${clause.op} ${value}`,
      };
    }),
    start: span.start,
    typed: clause.value,
    heading: `Values of ${prettyField(clause.field)}`,
  };
}

// ---------------------------------------------------------------------------

/** Prefix matches first, then substring — both case-insensitive. */
function rank(candidates: readonly string[], typed: string): string[] {
  const q = typed.trim().toLowerCase();
  if (q === '') return [...candidates];
  const starts: string[] = [];
  const contains: string[] = [];
  for (const c of candidates) {
    const lower = c.toLowerCase();
    if (lower.startsWith(q)) starts.push(c);
    else if (lower.includes(q)) contains.push(c);
  }
  return [...starts, ...contains];
}

/**
 * Field ranking sorts; VALUE ranking must not. Distinct values keep the order
 * they appear in the project, which is meaningful — alphabetising a usage list
 * throws away the order the scheme was built in.
 *
 * Engine-internal fields (`_height`, `_stackOrder`) sink to the bottom: they
 * sort first alphabetically and are the last thing anyone is looking for.
 */
function rankFields(candidates: readonly string[], typed: string): string[] {
  const order = (a: string, b: string): number => {
    const ai = a.startsWith('_') ? 1 : 0;
    const bi = b.startsWith('_') ? 1 : 0;
    return ai - bi || naturalCompare(a, b);
  };
  const q = typed.trim().toLowerCase();
  if (q === '') return [...candidates].sort(order);
  const starts: string[] = [];
  const contains: string[] = [];
  for (const c of candidates) {
    const lower = c.toLowerCase();
    if (lower.startsWith(q)) starts.push(c);
    else if (lower.includes(q)) contains.push(c);
  }
  return [...starts.sort(order), ...contains.sort(order)];
}

function sampleNote(data: GiraffeData, source: SourceName, field: string): string {
  for (const row of data.rows(source)) {
    const v = row.properties[field];
    if (v === null || v === undefined || v === '') continue;
    const shown = typeof v === 'number' ? formatSample(v) : String(v);
    return ` · e.g. ${shown.length > 24 ? `${shown.slice(0, 24)}…` : shown}`;
  }
  return '';
}

function formatSample(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function prettyLabel(source: SourceName): string {
  if (source === 'areas') return 'Areas';
  return source === 'bakedSections' ? 'baked sections' : source === 'rawSections' ? 'raw sections' : 'usages';
}
